import { useEffect, useRef, useState, type JSX } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useAdminInviter, useInviterSearch } from '@/hooks/useAdmin';
import { teamLabel } from '@/lib/labels';

/**
 * Pick an outreacher by searching the server (name or email), so the list
 * isn't capped at whoever fits in one page. Looks and behaves like
 * <Combobox>; the value is the outreacher's numeric id as a string ('' =
 * none / all).
 */
export function OutreacherPicker({
  value,
  onChange,
  team,
  activeOnly = false,
  allowAll = false,
  allLabel = 'All outreachers',
  placeholder = 'Search outreachers…',
  currentId,
  className,
  id,
  disabled = false,
}: {
  value: string;
  onChange: (id: string, name?: string) => void;
  /** Only outreachers on this team. */
  team?: string;
  activeOnly?: boolean;
  allowAll?: boolean;
  allLabel?: string;
  placeholder?: string;
  /** Marks this outreacher as "current" in the list (e.g. when reassigning). */
  currentId?: number;
  className?: string;
  id?: string;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 250);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useInviterSearch({ search: debounced, team, activeOnly }, open);
  const selectedId = /^\d+$/.test(value) ? Number(value) : null;
  const selected = useAdminInviter(selectedId);
  const displayValue = selected.data?.name ?? (value === '' && allowAll ? allLabel : value ? 'Loading…' : '');

  useEffect(() => {
    if (!open) return;
    function onDown(ev: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function pick(next: string, name?: string): void {
    onChange(next, name);
    setOpen(false);
    setSearch('');
  }

  const rows = results.data ?? [];

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-label="Outreacher"
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent px-3 pr-8 text-sm transition-colors duration-150 ease-out-quart pointer-coarse:h-11 pointer-coarse:text-base',
            'placeholder:text-muted-foreground/70',
            'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          placeholder={placeholder}
          value={open ? search : displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              setSearch('');
              inputRef.current?.blur();
            }
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className="absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded hover:bg-muted"
          onClick={() => {
            setOpen((v) => !v);
            if (!open) inputRef.current?.focus();
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Toggle outreacher list"
        >
          <ChevronDown
            className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform duration-150', open && 'rotate-180')}
          />
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[16rem] overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="max-h-[300px] overflow-auto p-1" role="listbox">
            {allowAll && !search.trim() && (
              <button
                type="button"
                onClick={() => pick('')}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent',
                  value === '' && 'bg-accent text-accent-foreground',
                )}
              >
                <span className="flex-1 truncate">{allLabel}</span>
                {value === '' && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            )}
            {results.isLoading ? (
              <div className="flex items-center justify-center gap-2 px-2 py-6 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : rows.length === 0 ? (
              <div className="px-2 py-6 text-center text-xs text-muted-foreground">No outreachers match</div>
            ) : (
              rows.map((o) => {
                const isSelected = String(o.id) === value;
                const hint = [teamLabel(o.team), o.id === currentId ? 'current' : '', o.isActive ? '' : 'inactive']
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => pick(String(o.id), o.name)}
                    className={cn(
                      'flex w-full select-none items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent text-accent-foreground',
                    )}
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{o.name}</span>
                      {o.email && <span className="truncate text-[11px] text-muted-foreground">{o.email}</span>}
                    </span>
                    {hint && <span className="shrink-0 text-[10px] text-muted-foreground">{hint}</span>}
                    {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
